"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import "@/app/auto-verkaufen/AutoVerkaufen.css";
import "@/common/Common.css";

import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";
import { TopSectionAutoVerkaufen } from "@/app/auto-verkaufen/sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";
import { fragenAutoExport } from "./data";
import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";

// Only needed after step 11; keep auth-related code/CSS out of initial paint.
// Below-the-fold section; defer loading to reduce initial route weight.
const Ratgeber = dynamic(() => import("@/components/Ratgeber/Ratgeber"), {
  ssr: false,
});

const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function KaputtesAuto() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionAutoVerkaufen
        title={
          <>
            Kaputtes Auto verkaufen: <br />
            schnell, sicher und zum Bestpreis
          </>
        }
        text={
          <>
            Verkaufe dein kaputtes Auto mit kostenloser
            <br />
            Abholung und schneller Bezahlung.
          </>
        }
        linkname="Kaputtes Auto Verkaufen"
        link="/kaputtes-auto-verkaufen"
        removeNav
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/kaputtes-auto-verkaufen"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Kaputtes Auto verkaufen - Wie funktioniert's?"
              leftText={
                <>
                  <p>
                    Wenn dein kaputtes oder defektes Auto die besten Tage hinter
                    sich hat und du darüber nachdenkst, es zu verkaufen, gibt es
                    verschiedene Möglichkeiten:
                  </p>

                  <p>
                    Traditionell müsstest du einen Händler aufsuchen oder zu
                    einem Käufer fahren, um den Restwert deines Autos zu
                    erfahren – ein Prozess, der oft mehrere Wochen dauert. Doch
                    bei uns geht das stressfrei: Gib einfach deine
                    Fahrgestellnummer und die Kilometerzahl ein, und wir
                    verbinden dich mit unserem Händlernetzwerk aus über 5.000
                    Partnern, um den besten Preis für dein kaputtes Auto zu
                    erzielen. Der Händler mit dem höchsten Angebot kauft dein
                    kaputtes Auto unkompliziert an und holt es kostenlos bei dir
                    ab.
                  </p>

                  <h3>Was gilt als kaputtes oder defektes Auto?</h3>

                  <ul className="custom-unordered-list secondary">
                    <li>Unfallschaden</li>
                    <li>Motorschaden</li>
                    <li>Getriebeschaden</li>
                    <li>Zylinderkopfschaden</li>
                    <li>Zahnriemenschaden</li>
                    <li>Karosserieschaden</li>
                    <li>Hagelschaden</li>
                  </ul>
                </>
              }
              rightFirstTitle="Kaputtes Auto mit Autoeinfachlos verkaufen"
              rightFirstText={
                <>
                  <p>
                    Es gibt viele Gründe, warum du dein defektes oder kaputtes
                    Auto mit Autoeinfachlos verkaufen solltest:
                  </p>

                  <ul className="custom-unordered-list ">
                    <li>
                      Du bekommst den besten Preis durch unser Händlernetzwerk
                      mit über 5.000 Partnern
                    </li>
                    <li>
                      Dein kaputtes Auto wird kostenlos direkt bei dir abgeholt
                    </li>
                    <li>Die Bezahlung erfolgt schnell und unkompliziert</li>
                    <li>Der Verkauf ist innerhalb von 24 Stunden möglich</li>
                  </ul>

                  <p>
                    Wenn das deinen Erwartungen entspricht, ist Autoeinfachlos
                    die ideale Lösung, um dein kaputtes Auto schnell, sicher und
                    ohne Stress zu verkaufen.
                  </p>
                </>
              }
              rightSecondTitle="Bereit, dein kaputtes Auto loszuwerden?"
              rightSecondText={
                <>
                  <p>
                    Autoeinfachlos arbeitet mit über 5.000 Autohändlern, die
                    garantieren, dein Fahrzeug unabhängig vom Schaden oder der
                    Fahrtüchtigkeit abzunehmen. So einfach funktioniert’s:
                  </p>

                  <ul className="custom-unordered-list secondary">
                    <li>Fahrgestellnummer eingeben</li>
                    <li>Bewertung erhalten</li>
                    <li>Auto verkaufen und abholen lassen</li>
                  </ul>

                  <p>
                    Wie die Abmeldung deines Fahrzeugs abläuft und welche
                    Unterlagen du dafür brauchst, erklärt unser Guide:
                  </p>
                  <p>
                    👉{" "}
                    <Link className="link" href="/guides/auto-abmelden">
                      Auto abmelden in Österreich
                    </Link>
                  </p>
                </>
              }
            />
            <div className="Banner-sections">
              <BildTextInfo
                // image={BannerImage1}
                revert
                isBig
                fontSize="36px"
                h2
                alt="Auto Export Banner Preis"
                title="Kaputtes Auto verkaufen zum Bestpreis"
                text={
                  <>
                    <p>
                      Wenn du das beste Angebot für dein kaputtes Auto suchst,
                      bist du hier genau richtig. Beim Verkauf deines defekten
                      Autos über Autoeinfachlos konkurrieren Händler, um dir den
                      besten Preis anzubieten. Sobald du dein Angebot annimmst,
                      profitierst du von einer kostenlosen Abholung deines Autos
                      und einer schnellen Überweisung des Geldes direkt auf dein
                      Bankkonto.
                    </p>

                    <p>
                      Der gesamte Prozess ist einfach, kann direkt über dein
                      Handy abgewickelt werden, und du musst nicht stundenlang
                      eine Kleinanzeige für dein Auto erstellen. Lies weiter, um
                      zu erfahren, wie es funktioniert.
                    </p>
                  </>
                }
              />

              <BildTextInfo
                // image={BannerImage2}
                isBig
                title="Kaputtes Auto verkaufen mit kostenloser Abholung"
                alt="Privater Autoexport vs Export mit einem Profi"
                fontSize="36px"
                h2
                text={
                  <>
                    <p>
                      Eines der größten Probleme beim Verkauf eines defekten
                      oder kaputten Autos ist die Überführung. Viele
                      Ankaufsplattformen erwarten, dass du das Fahrzeug selbst
                      zu einer Filiale bringst.
                    </p>
                    <p>
                      Bei Autoeinfachlos machen wir es anders: Wenn du dein Auto
                      an unser landesweites Netzwerk geprüfter
                      Gebrauchtwagenhändler verkaufst, ist die kostenlose
                      Abholung immer inklusive. Sobald dein Fahrzeug verkauft
                      ist, wird es an einem Datum, einer Uhrzeit und einem Ort
                      abgeholt, die dir am besten passen. Das kann dein Zuhause,
                      dein Arbeitsplatz oder ein anderer bequemer Ort sein –
                      unabhängig davon, wo du und der Käufer in Österreich seid.
                    </p>
                  </>
                }
              />
            </div>

            <HäufigeFragen
              title="Häufig gestellte Fragen: Kaputtes oder defektes Auto verkaufen"
              fragen={fragenAutoExport}
              image={FAQImage}
            />
            <Ratgeber
              title="Mehr zum Thema kaputtes Auto verkaufen"
              unfallAuto
              autoMitMotorschaden
              autoExport
              autoVerkaufen
              autoBewerten
              listenpreisBerechnen
            />
          </div>
          <Banner title="Verkaufe dein Kaputtes Auto mit Autoeinfachlos" />
        </div>
      )}
    </div>
  );
}

export default KaputtesAuto;
